import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Input from "../inputs/inputs";
import Button from "../buttons/buttons";
//images
import CLOSE from "../../assets/images/icons/close.svg";

const isValidUrl = (url) => /^https?:\/\/\S+$/i.test(url);

export function InsertImageUrlModal({ show, onClose, onInsert }) {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | loaded | error

  // Reset when the modal opens
  useEffect(() => {
    if (show) {
      setUrl("");
      setStatus("idle");
    }
  }, [show]);

  const trimmedUrl = url.trim();

  const handleChange = (e) => {
    setUrl(e.target.value);
    setStatus(isValidUrl(e.target.value.trim()) ? "loading" : "idle");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // The dialog is portaled, but React still bubbles events to any parent form
    e.stopPropagation();
    if (status !== "loaded") return;
    onInsert(trimmedUrl);
  };

  return (
    <Transition show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full bg-white dark:bg-[#121212] rounded-3xl px-4 pt-5 pb-4 max-h-[90vh] overflow-y-auto shadow-xl transform transition-all sm:my-10 sm:max-w-lg sm:p-8">
                <div className="absolute top-1 sm:top-3 right-4 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-[#ffeae5] rounded-3xl p-2 sm:p-3 outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <img
                      src={CLOSE}
                      alt="close"
                      className="w-3 h-3 sm:w-4 sm:h-4"
                    />
                  </button>
                </div>
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium text-black dark:text-white sm:mt-2"
                >
                  Insert Image
                </Dialog.Title>
                <div className="mt-4 border-b-2"></div>

                <form className="mt-8" onSubmit={handleSubmit}>
                  <Input.Label
                    title={"Image URL"}
                    type={"url"}
                    name={"imageUrl"}
                    placeholder={"https://example.com/image.png"}
                    onChange={handleChange}
                    value={url}
                    subText={"Use a publicly accessible link so the image shows in recipients' inboxes."}
                  />

                  {isValidUrl(trimmedUrl) && (
                    <div className="mb-6">
                      <img
                        key={trimmedUrl}
                        src={trimmedUrl}
                        alt="preview"
                        onLoad={() => setStatus("loaded")}
                        onError={() => setStatus("error")}
                        className={`max-h-48 mx-auto rounded-lg ${status === "loaded" ? "" : "hidden"}`}
                      />
                      {status === "loading" && (
                        <p className="text-sm text-gray-500 dark:text-[#BCBCBC] text-center">
                          Loading preview...
                        </p>
                      )}
                      {status === "error" && (
                        <p className="text-sm text-primary text-center">
                          Couldn't load an image from this URL.
                        </p>
                      )}
                    </div>
                  )}

                  <Button.Primary
                    type="submit"
                    disabled={status !== "loaded"}
                    className="bg-primary text-white w-full h-12 rounded-lg"
                    title={"Insert Image"}
                  />
                  <Button.Secondary
                    type="button"
                    className="w-full h-12 rounded-lg mt-5"
                    title={"Cancel"}
                    onClick={onClose}
                  />
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
